import { useState, useEffect, useCallback, useRef } from "react";
import QMSLayout from "../components/QMSLayout";
import { useTranslation } from "react-i18next";
import {
  Network, Plus, Trash2, CheckCircle2, XCircle,
  RefreshCw, Loader2, X, Plug, Zap, ArrowLeftRight, ArrowDown,
  ArrowUp, Clock, ChevronDown, ChevronUp,
  AlertTriangle, History, TestTube, Upload, Download,
  FileText, FileSpreadsheet, Code, Globe, Link2, Copy,
  Filter, Search
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  configured: "text-amber-400 bg-amber-400/10 border-amber-400/30",
  connected: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  error: "text-red-400 bg-red-400/10 border-red-400/30",
};

const syncModeIcons = { pull: ArrowDown, push: ArrowUp, bidirectional: ArrowLeftRight };

const phaseLabels = { 1: "Phase 1", 2: "Phase 2", 3: "Phase 3" };
const phaseColors = {
  1: "bg-emerald-400/10 text-emerald-400 border-emerald-400/30",
  2: "bg-amber-400/10 text-amber-400 border-amber-400/30",
  3: "bg-slate-600/30 text-slate-400 border-slate-600",
};

const TABS = [
  { id: "catalog", label: "Connectors", icon: Plug },
  { id: "file_bridge", label: "File Bridge", icon: FileText },
  { id: "webhooks", label: "Webhooks", icon: Globe },
];

const ConnectorsPage = () => {
  const { t } = useTranslation(['platform', 'common']);
  const [activeTab, setActiveTab] = useState("catalog");
  const [registry, setRegistry] = useState(null);
  const [connections, setConnections] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [setupStep, setSetupStep] = useState(1);
  const [testing, setTesting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [expandedConn, setExpandedConn] = useState(null);
  const [industryFilter, setIndustryFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [form, setForm] = useState({
    name: "", auth_method: "", sync_mode: "pull", sync_interval: "manual",
    credentials: {}, enabled_capabilities: [],
  });

  // File Bridge state
  const [imports, setImports] = useState([]);
  const [exports, setExports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [exportingModule, setExportingModule] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ source_system: "", module: "documents", notes: "" });

  // Webhook state
  const [webhooks, setWebhooks] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [webhookForm, setWebhookForm] = useState({ connector_id: "", client_label: "", description: "" });
  const [copiedId, setCopiedId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [regRes, connRes, logRes, impRes, expRes, whRes, whLogRes] = await Promise.all([
        fetch(`${API}/api/connectors/registry`, { credentials: "include" }),
        fetch(`${API}/api/connectors/connections`, { credentials: "include" }),
        fetch(`${API}/api/connectors/sync-log?limit=20`, { credentials: "include" }),
        fetch(`${API}/api/connectors/file-bridge/imports?limit=30`, { credentials: "include" }),
        fetch(`${API}/api/connectors/file-bridge/exports?limit=30`, { credentials: "include" }),
        fetch(`${API}/api/connectors/webhooks`, { credentials: "include" }),
        fetch(`${API}/api/connectors/webhooks/log?limit=20`, { credentials: "include" }),
      ]);
      if (regRes.ok) setRegistry(await regRes.json());
      if (connRes.ok) { const d = await connRes.json(); setConnections(d.connections || []); }
      if (logRes.ok) { const d = await logRes.json(); setSyncLogs(d.logs || []); }
      if (impRes.ok) { const d = await impRes.json(); setImports(d.imports || []); }
      if (expRes.ok) { const d = await expRes.json(); setExports(d.exports || []); }
      if (whRes.ok) { const d = await whRes.json(); setWebhooks(d.webhooks || []); }
      if (whLogRes.ok) { const d = await whLogRes.json(); setWebhookLogs(d.logs || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const connectedIds = connections.map(c => c.connector_id);
  const capLabels = registry?.capability_labels || {};
  const industryLabels = registry?.industry_labels || {};
  const intTypeLabels = registry?.integration_type_labels || {};

  // Filtered connectors
  const allConnectors = registry?.connectors || [];
  const filteredConnectors = allConnectors.filter(c => {
    if (industryFilter !== "all" && !(c.industries || []).includes(industryFilter)) return false;
    if (phaseFilter !== "all" && c.phase !== parseInt(phaseFilter)) return false;
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase()) && !c.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const allIndustries = [...new Set(allConnectors.flatMap(c => c.industries || []))];

  // Connector setup handlers
  const startSetup = (connector) => {
    setSelectedConnector(connector);
    setForm({
      name: connector.name, auth_method: connector.auth_methods[0],
      sync_mode: connector.sync_modes?.includes("bidirectional") ? "bidirectional" : (connector.sync_modes?.[0] || "pull"),
      sync_interval: "manual", credentials: {},
      enabled_capabilities: [...connector.capabilities],
    });
    setSetupStep(1);
    setShowSetup(true);
  };

  const handleSave = async () => {
    try {
      const res = await fetch(`${API}/api/connectors/connections`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ connector_id: selectedConnector.id, name: form.name, auth_method: form.auth_method, sync_mode: form.sync_mode, sync_interval: form.sync_interval, credentials: form.credentials, enabled_capabilities: form.enabled_capabilities }),
      });
      if (res.ok) { setShowSetup(false); fetchAll(); }
    } catch (e) { console.error(e); }
  };

  const handleTest = async (connectionId) => { setTesting(connectionId); try { await fetch(`${API}/api/connectors/connections/${connectionId}/test`, { method: "POST", credentials: "include" }); fetchAll(); } catch (e) { console.error(e); } setTesting(null); };
  const handleSync = async (connectionId) => { setSyncing(connectionId); try { await fetch(`${API}/api/connectors/connections/${connectionId}/sync`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }); fetchAll(); } catch (e) { console.error(e); } setSyncing(null); };
  const handleDeleteConn = async (connectionId) => { if (!window.confirm("Remove this connector?")) return; try { await fetch(`${API}/api/connectors/connections/${connectionId}`, { method: "DELETE", credentials: "include" }); fetchAll(); } catch (e) { console.error(e); } };

  // File Bridge handlers
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("source_system", uploadForm.source_system || "unknown");
    fd.append("module", uploadForm.module);
    fd.append("notes", uploadForm.notes);
    try {
      const res = await fetch(`${API}/api/connectors/file-bridge/import`, { method: "POST", credentials: "include", body: fd });
      if (res.ok) { fetchAll(); setUploadForm({ source_system: "", module: "documents", notes: "" }); }
    } catch (e) { console.error(e); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleExport = async (module, format) => {
    setExportingModule(module);
    try {
      const res = await fetch(`${API}/api/connectors/file-bridge/export`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ module, format }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = res.headers.get("content-disposition")?.split("filename=")[1] || `charstan_${module}.${format}`;
        a.click(); URL.revokeObjectURL(url);
        fetchAll();
      }
    } catch (e) { console.error(e); }
    setExportingModule(null);
  };

  const handleDeleteImport = async (importId) => { if (!window.confirm("Delete this import?")) return; try { await fetch(`${API}/api/connectors/file-bridge/imports/${importId}`, { method: "DELETE", credentials: "include" }); fetchAll(); } catch (e) { console.error(e); } };

  // Webhook handlers
  const handleCreateWebhook = async () => {
    try {
      const res = await fetch(`${API}/api/connectors/webhooks`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(webhookForm),
      });
      if (res.ok) { setShowWebhookForm(false); setWebhookForm({ connector_id: "", client_label: "", description: "" }); fetchAll(); }
    } catch (e) { console.error(e); }
  };

  const handleDeleteWebhook = async (webhookId) => { if (!window.confirm("Delete this webhook endpoint?")) return; try { await fetch(`${API}/api/connectors/webhooks/${webhookId}`, { method: "DELETE", credentials: "include" }); fetchAll(); } catch (e) { console.error(e); } };

  const copyWebhookUrl = (webhookId) => {
    navigator.clipboard.writeText(`${API}/api/webhooks/${webhookId}`);
    setCopiedId(webhookId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fileIcon = (ext) => {
    if ([".xlsx", ".csv"].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-emerald-400" />;
    if ([".xml", ".json"].includes(ext)) return <Code className="w-4 h-4 text-amber-400" />;
    return <FileText className="w-4 h-4 text-cyan-400" />;
  };

  const formatBytes = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

  if (loading) {
    return (
      <QMSLayout title={t('platform:connectors.title')}>
        <div className="flex items-center justify-center h-64" data-testid="connectors-loading">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('platform:connectors.title')}>
      <div className="space-y-6" data-testid="connectors-page">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Network className="w-6 h-6 text-cyan-400" />
            CHARSTAN Integration Hub
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Connect your QMS via File Bridge, Webhooks, or Direct API — {allConnectors.length} platforms supported
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-900/80 p-1 rounded-xl border border-slate-700/60 w-fit">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30" : "text-slate-400 hover:text-slate-200 border border-transparent"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
                {tab.id === "catalog" && connections.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-cyan-500/20 text-cyan-300">{connections.length}</span>
                )}
                {tab.id === "webhooks" && webhooks.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-cyan-500/20 text-cyan-300">{webhooks.length}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ===== CATALOG TAB ===== */}
        {activeTab === "catalog" && (
          <div className="space-y-6">
            {/* Active Connections */}
            {connections.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Active Connections ({connections.length})</h2>
                {connections.map((conn) => {
                  const SyncIcon = syncModeIcons[conn.sync_mode] || ArrowLeftRight;
                  const isExpanded = expandedConn === conn.connection_id;
                  return (
                    <div key={conn.connection_id} className="bg-slate-900/80 border border-slate-700/60 rounded-xl overflow-hidden" data-testid={`connection-card-${conn.connector_id}`}>
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center"><Plug className="w-5 h-5 text-cyan-400" /></div>
                          <div>
                            <h3 className="text-white font-medium">{conn.name}</h3>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${statusColors[conn.status] || statusColors.configured}`} data-testid={`connection-status-${conn.connector_id}`}>
                                {conn.status === "connected" ? <CheckCircle2 className="w-3 h-3" /> : conn.status === "error" ? <XCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                                {conn.status}
                              </span>
                              <span className="flex items-center gap-1"><SyncIcon className="w-3 h-3" /> {conn.sync_mode}</span>
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {conn.sync_interval}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleTest(conn.connection_id)} disabled={testing === conn.connection_id} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-800 transition-colors disabled:opacity-50" title="Test" data-testid={`test-btn-${conn.connector_id}`}>
                            {testing === conn.connection_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleSync(conn.connection_id)} disabled={syncing === conn.connection_id} className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition-colors disabled:opacity-50" title="Sync" data-testid={`sync-btn-${conn.connector_id}`}>
                            {syncing === conn.connection_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </button>
                          <button onClick={() => setExpandedConn(isExpanded ? null : conn.connection_id)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" data-testid={`expand-btn-${conn.connector_id}`}>
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDeleteConn(conn.connection_id)} className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors" title="Remove" data-testid={`delete-btn-${conn.connector_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="border-t border-slate-700/60 p-4 bg-slate-900/50 space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div><span className="text-slate-500 block text-xs">Auth</span><span className="text-slate-200">{conn.auth_method}</span></div>
                            <div><span className="text-slate-500 block text-xs">Total Synced</span><span className="text-slate-200">{conn.sync_stats?.total_synced || 0}</span></div>
                            <div><span className="text-slate-500 block text-xs">Last Sync</span><span className="text-slate-200">{conn.last_sync ? new Date(conn.last_sync).toLocaleString() : "Never"}</span></div>
                            <div><span className="text-slate-500 block text-xs">Pull / Push</span><span className="text-slate-200">{conn.sync_stats?.last_records_pulled || 0} / {conn.sync_stats?.last_records_pushed || 0}</span></div>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs block mb-1">Enabled Modules</span>
                            <div className="flex flex-wrap gap-1.5">
                              {(conn.enabled_capabilities || []).map(cap => (<span key={cap} className="px-2 py-0.5 text-xs rounded bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">{capLabels[cap] || cap}</span>))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search connectors..." className="w-full bg-slate-900/80 border border-slate-700/60 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none" data-testid="connector-search" />
              </div>
              <select value={industryFilter} onChange={e => setIndustryFilter(e.target.value)} className="bg-slate-900/80 border border-slate-700/60 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" data-testid="industry-filter">
                <option value="all">All Industries</option>
                {allIndustries.map(ind => (<option key={ind} value={ind}>{industryLabels[ind] || ind}</option>))}
              </select>
              <select value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)} className="bg-slate-900/80 border border-slate-700/60 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" data-testid="phase-filter">
                <option value="all">All Phases</option>
                <option value="1">Phase 1 — Build Now</option>
                <option value="2">Phase 2 — Next Sprint</option>
                <option value="3">Phase 3 — On Demand</option>
              </select>
              <span className="text-xs text-slate-500">{filteredConnectors.length} of {allConnectors.length}</span>
            </div>

            {/* Connector Catalog Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredConnectors.map((connector) => {
                const isConnected = connectedIds.includes(connector.id);
                return (
                  <div key={connector.id} className={`bg-slate-900/80 border rounded-xl p-5 transition-all ${isConnected ? "border-emerald-500/30 opacity-75" : "border-slate-700/60 hover:border-cyan-500/40 hover:shadow-lg hover:shadow-cyan-500/5"}`} data-testid={`catalog-card-${connector.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center"><Plug className="w-5 h-5 text-cyan-400" /></div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${phaseColors[connector.phase]}`}>{phaseLabels[connector.phase]}</span>
                        {isConnected && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/30">Active</span>}
                      </div>
                    </div>
                    <h3 className="text-white font-semibold text-base mb-1">{connector.name}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed mb-2 line-clamp-2">{connector.description}</p>

                    {/* Industries */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(connector.industries || []).slice(0, 3).map(ind => (
                        <span key={ind} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800/80 text-slate-500 border border-slate-700/50">{industryLabels[ind] || ind}</span>
                      ))}
                    </div>

                    {/* Integration types */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(connector.integration_types || []).map(it => (
                        <span key={it} className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/5 text-cyan-400/70 border border-cyan-500/15">{intTypeLabels[it] || it}</span>
                      ))}
                    </div>

                    {/* Capabilities */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {connector.capabilities.slice(0, 3).map(cap => (<span key={cap} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">{capLabels[cap] || cap}</span>))}
                      {connector.capabilities.length > 3 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-500">+{connector.capabilities.length - 3}</span>}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                        {connector.auth_methods.map(m => (<span key={m} className="flex items-center gap-0.5">{m}</span>))}
                      </div>
                      {!isConnected && (
                        <button onClick={() => startSetup(connector)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors" data-testid={`setup-btn-${connector.id}`}>
                          <Plus className="w-3 h-3" /> Connect
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sync Log */}
            {syncLogs.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2"><History className="w-4 h-4" /> Recent Activity</h2>
                <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl overflow-hidden divide-y divide-slate-800">
                  {syncLogs.slice(0, 8).map((log, i) => (
                    <div key={i} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`sync-log-entry-${i}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${log.status === "success" ? "bg-emerald-400" : "bg-red-400"}`} />
                        <span className="text-slate-300 font-medium">{log.connector_id}</span>
                        <span className="text-slate-500">{log.action}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="max-w-xs truncate">{log.message}</span>
                        <span>{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== FILE BRIDGE TAB ===== */}
        {activeTab === "file_bridge" && (
          <div className="space-y-6">
            {/* Import Section */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2"><Upload className="w-4 h-4" /> Universal Import</h2>
              <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl p-5">
                <p className="text-sm text-slate-400 mb-4">Upload files exported from any external QMS. Supported: PDF, DOCX, XLSX, CSV, XML, JSON</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Source System</label>
                    <input value={uploadForm.source_system} onChange={e => setUploadForm({ ...uploadForm, source_system: e.target.value })} placeholder="e.g. MasterControl, SAP QM" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none" data-testid="import-source-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Module</label>
                    <select value={uploadForm.module} onChange={e => setUploadForm({ ...uploadForm, module: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" data-testid="import-module-select">
                      <option value="documents">Document Control</option>
                      <option value="capa">CAPA Records</option>
                      <option value="audits">Audit Records</option>
                      <option value="deviations">Deviations</option>
                      <option value="training">Training</option>
                      <option value="suppliers">Suppliers</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Notes</label>
                    <input value={uploadForm.notes} onChange={e => setUploadForm({ ...uploadForm, notes: e.target.value })} placeholder="Optional notes" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none" data-testid="import-notes-input" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input type="file" ref={fileInputRef} accept=".pdf,.docx,.xlsx,.csv,.xml,.json,.txt" onChange={handleUpload} className="hidden" data-testid="import-file-input" />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors disabled:opacity-50" data-testid="import-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? t('common:status.uploading') : t('platform:connectors.chooseFile')}
                  </button>
                </div>
              </div>

              {/* Import History */}
              {imports.length > 0 && (
                <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-800 text-xs font-semibold text-slate-400 uppercase">Recent Imports ({imports.length})</div>
                  <div className="divide-y divide-slate-800">
                    {imports.map(imp => (
                      <div key={imp.import_id} className="px-4 py-3 flex items-center justify-between" data-testid={`import-row-${imp.import_id}`}>
                        <div className="flex items-center gap-3">
                          {fileIcon(imp.file_ext)}
                          <div>
                            <span className="text-sm text-white">{imp.filename}</span>
                            <div className="text-xs text-slate-500 flex items-center gap-2">
                              <span>{formatBytes(imp.file_size)}</span>
                              {imp.source_system !== "unknown" && <span>from {imp.source_system}</span>}
                              {imp.record_count != null && <span>{imp.record_count} records</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">{new Date(imp.created_at).toLocaleString()}</span>
                          <button onClick={() => handleDeleteImport(imp.import_id)} className="p-1 text-slate-500 hover:text-red-400 transition-colors" data-testid={`delete-import-${imp.import_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Export Section */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2"><Download className="w-4 h-4" /> Universal Export</h2>
              <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl p-5">
                <p className="text-sm text-slate-400 mb-4">Export CHARSTAN data for import into any target system.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { module: "gap_assessments", label: "Gap Assessments" },
                    { module: "audits", label: "Audit Records" },
                    { module: "capa", label: "CAPA Records" },
                    { module: "deviations", label: "Deviations" },
                    { module: "documents", label: "Client Documents" },
                    { module: "compliance_alerts", label: "Compliance Alerts" },
                  ].map(({ module, label }) => (
                    <div key={module} className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4" data-testid={`export-card-${module}`}>
                      <h3 className="text-sm text-white font-medium mb-2">{label}</h3>
                      <div className="flex gap-2">
                        {["csv", "json", "xml"].map(fmt => (
                          <button key={fmt} onClick={() => handleExport(module, fmt)} disabled={exportingModule === module}
                            className="px-2.5 py-1 text-xs rounded border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-colors disabled:opacity-50 uppercase"
                            data-testid={`export-btn-${module}-${fmt}`}
                          >
                            {exportingModule === module ? <Loader2 className="w-3 h-3 animate-spin inline" /> : fmt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Export History */}
              {exports.length > 0 && (
                <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-800 text-xs font-semibold text-slate-400 uppercase">Recent Exports ({exports.length})</div>
                  <div className="divide-y divide-slate-800">
                    {exports.map(exp => (
                      <div key={exp.export_id} className="px-4 py-3 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3">
                          <Download className="w-4 h-4 text-emerald-400" />
                          <span className="text-slate-200">{exp.module}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 uppercase">{exp.format}</span>
                          <span className="text-xs text-slate-500">{exp.record_count} records</span>
                        </div>
                        <span className="text-xs text-slate-500">{new Date(exp.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== WEBHOOKS TAB ===== */}
        {activeTab === "webhooks" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2"><Globe className="w-4 h-4" /> Webhook Endpoints</h2>
                <p className="text-xs text-slate-500 mt-1">Give these URLs to clients — their QMS sends events here automatically</p>
              </div>
              <button onClick={() => setShowWebhookForm(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors" data-testid="create-webhook-btn">
                <Plus className="w-4 h-4" /> New Endpoint
              </button>
            </div>

            {/* Webhook List */}
            {webhooks.length === 0 ? (
              <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl p-12 text-center">
                <Globe className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No webhook endpoints configured yet</p>
                <p className="text-slate-500 text-xs mt-1">Create an endpoint and share the URL with your client's QMS administrator</p>
              </div>
            ) : (
              <div className="space-y-3">
                {webhooks.map(wh => (
                  <div key={wh.webhook_id} className="bg-slate-900/80 border border-slate-700/60 rounded-xl p-4" data-testid={`webhook-card-${wh.webhook_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                          <Link2 className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium text-sm">{wh.client_label}</h3>
                          <span className="text-xs text-slate-500">{wh.connector_id} {wh.description && `— ${wh.description}`}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{wh.events_received} events</span>
                        {wh.last_event && <span>Last: {new Date(wh.last_event).toLocaleString()}</span>}
                        <button onClick={() => handleDeleteWebhook(wh.webhook_id)} className="p-1 text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-800/80 rounded-lg px-3 py-2 border border-slate-700/50">
                      <code className="text-xs text-cyan-300 flex-1 truncate" data-testid={`webhook-url-${wh.webhook_id}`}>{`${API}/api/webhooks/${wh.webhook_id}`}</code>
                      <button onClick={() => copyWebhookUrl(wh.webhook_id)} className="p-1 text-slate-400 hover:text-cyan-400 transition-colors" data-testid={`copy-webhook-${wh.webhook_id}`}>
                        {copiedId === wh.webhook_id ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Webhook Event Log */}
            {webhookLogs.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2"><History className="w-4 h-4" /> Incoming Events</h2>
                <div className="bg-slate-900/80 border border-slate-700/60 rounded-xl overflow-hidden divide-y divide-slate-800">
                  {webhookLogs.map((log, i) => (
                    <div key={i} className="px-4 py-3 text-sm" data-testid={`webhook-log-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full bg-emerald-400" />
                          <span className="text-slate-300 font-medium">{log.client_label || log.webhook_id}</span>
                          <span className="text-xs text-slate-500">{log.connector_id}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{log.content_type?.split(";")[0]}</span>
                        </div>
                        <span className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      {log.payload && (
                        <pre className="text-xs text-slate-500 bg-slate-800/50 rounded p-2 mt-1 overflow-x-auto max-h-20">
                          {JSON.stringify(log.payload, null, 2).slice(0, 300)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create Webhook Modal */}
            {showWebhookForm && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" data-testid="webhook-modal">
                <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md mx-4">
                  <div className="p-5 border-b border-slate-800 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-white">Create Webhook Endpoint</h2>
                    <button onClick={() => setShowWebhookForm(false)} className="p-1 rounded text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="text-sm text-slate-300 block mb-1">Client Name</label>
                      <input value={webhookForm.client_label} onChange={e => setWebhookForm({ ...webhookForm, client_label: e.target.value })} placeholder="e.g. Acme Corp" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none" data-testid="webhook-client-input" />
                    </div>
                    <div>
                      <label className="text-sm text-slate-300 block mb-1">Source System</label>
                      <select value={webhookForm.connector_id} onChange={e => setWebhookForm({ ...webhookForm, connector_id: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" data-testid="webhook-connector-select">
                        <option value="">Select system...</option>
                        {allConnectors.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-slate-300 block mb-1">Description (optional)</label>
                      <input value={webhookForm.description} onChange={e => setWebhookForm({ ...webhookForm, description: e.target.value })} placeholder="e.g. CAPA change notifications" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none" data-testid="webhook-desc-input" />
                    </div>
                  </div>
                  <div className="p-5 border-t border-slate-800 flex justify-end gap-3">
                    <button onClick={() => setShowWebhookForm(false)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white">{t('common:actions.cancel')}</button>
                    <button onClick={handleCreateWebhook} disabled={!webhookForm.connector_id || !webhookForm.client_label}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-cyan-500 text-white hover:bg-cyan-600 transition-colors disabled:opacity-50" data-testid="webhook-save-btn">
                      Create Endpoint
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== CONNECTOR SETUP MODAL ===== */}
        {showSetup && selectedConnector && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" data-testid="setup-modal">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
              <div className="p-5 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">Connect {selectedConnector.name}</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Step {setupStep} of 3</p>
                </div>
                <button onClick={() => setShowSetup(false)} className="p-1 rounded text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                {setupStep === 1 && (
                  <>
                    <div>
                      <label className="text-sm text-slate-300 block mb-1">Connection Name</label>
                      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" data-testid="setup-name-input" />
                    </div>
                    <div>
                      <label className="text-sm text-slate-300 block mb-1">Authentication Method</label>
                      <div className="flex gap-2">
                        {selectedConnector.auth_methods.map(m => (
                          <button key={m} onClick={() => setForm({ ...form, auth_method: m, credentials: {} })} className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${form.auth_method === m ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-400" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`} data-testid={`auth-method-${m}`}>
                            {m === "api_key" ? "API Key" : "OAuth 2.0"}
                          </button>
                        ))}
                      </div>
                    </div>
                    {Object.entries(selectedConnector.config_fields || {}).map(([fieldKey, field]) => {
                      if (!field.required_for?.includes(form.auth_method)) return null;
                      return (
                        <div key={fieldKey}>
                          <label className="text-sm text-slate-300 block mb-1">{field.label}</label>
                          <input type={field.type === "password" ? "password" : "text"} placeholder={field.placeholder || ""} value={form.credentials[fieldKey] || ""} onChange={e => setForm({ ...form, credentials: { ...form.credentials, [fieldKey]: e.target.value } })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-cyan-500 focus:outline-none" data-testid={`cred-field-${fieldKey}`} />
                        </div>
                      );
                    })}
                  </>
                )}
                {setupStep === 2 && (
                  <>
                    <div>
                      <label className="text-sm text-slate-300 block mb-1">Sync Direction</label>
                      <div className="flex gap-2">
                        {(selectedConnector.sync_modes || []).map(mode => {
                          const Icon = syncModeIcons[mode] || ArrowLeftRight;
                          return (
                            <button key={mode} onClick={() => setForm({ ...form, sync_mode: mode })} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${form.sync_mode === mode ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-400" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`} data-testid={`sync-mode-${mode}`}>
                              <Icon className="w-4 h-4" />{mode}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-300 block mb-1">Sync Interval</label>
                      <select value={form.sync_interval} onChange={e => setForm({ ...form, sync_interval: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" data-testid="sync-interval-select">
                        {(registry?.sync_intervals || []).map(si => (<option key={si} value={si}>{si === "manual" ? t('platform:connectors.manualOnly') : t('platform:connectors.every', { interval: si })}</option>))}
                      </select>
                    </div>
                  </>
                )}
                {setupStep === 3 && (
                  <>
                    <p className="text-sm text-slate-400">Select modules to sync with {selectedConnector.name}:</p>
                    <div className="space-y-2">
                      {selectedConnector.capabilities.map(cap => (
                        <label key={cap} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800 border border-slate-700 cursor-pointer hover:border-slate-600 transition-colors">
                          <input type="checkbox" checked={form.enabled_capabilities.includes(cap)} onChange={() => setForm(prev => ({ ...prev, enabled_capabilities: prev.enabled_capabilities.includes(cap) ? prev.enabled_capabilities.filter(c => c !== cap) : [...prev.enabled_capabilities, cap] }))} className="rounded border-slate-600 text-cyan-500 focus:ring-cyan-500" data-testid={`cap-checkbox-${cap}`} />
                          <span className="text-sm text-slate-200">{capLabels[cap] || cap}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="p-5 border-t border-slate-800 flex justify-between">
                <button onClick={() => setupStep > 1 ? setSetupStep(setupStep - 1) : setShowSetup(false)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white" data-testid="setup-back-btn">{setupStep > 1 ? "Back" : "Cancel"}</button>
                {setupStep < 3 ? (
                  <button onClick={() => setSetupStep(setupStep + 1)} className="px-4 py-2 rounded-lg text-sm font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/30 transition-colors" data-testid="setup-next-btn">Next</button>
                ) : (
                  <button onClick={handleSave} className="px-4 py-2 rounded-lg text-sm font-medium bg-cyan-500 text-white hover:bg-cyan-600 transition-colors" data-testid="setup-save-btn">
                    <Zap className="w-4 h-4 inline mr-1" />Activate Connector
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default ConnectorsPage;
